import BillingModel from "../../../types/billing-model";
import BillingFormData, {
  BillingFormDataContributions,
} from "../types/billing-form.data";
import BillingsNode from "../../../common/types/billings-node";
import googleMapsRouteService from "../../../../../common/utils/google-maps-route/google-maps-route.service";
import BillingsTaxiTaxiContributionBonus, {
  BillingsTaxiTaxiContributionBonusType,
} from "../../common/contributions/bonus/types/billings-taxi-taxi-contributions-bonus";
import BillingsTaxiTaxiContributionPenalty, {
  BillingsTaxiTaxiContributionPenaltyType,
} from "../../common/contributions/penalty/types/billings-taxi-taxi-contributions-penalty";
import TabsData from "../../../../../common/components/tabs/common/types/tabs-data";
import billingsTranslationsHelper from "../../../../../languages/billings-translations.helper";
import BillingsTaxiTaxiContributionsBonusTableRow from "../../common/contributions/bonus/types/billings-taxi-taxi-contributions-bonus-table-row";
import BillingsTaxiTaxiContributionsPenaltyTableRow from "../../common/contributions/penalty/types/billings-taxi-taxi-contributions-penalty-table-row";
import BillingsTaxiTaxiContributionsBonusTableComponent from "../../common/contributions/bonus/table/billings-taxi-taxi-contributions-bonus-table.component";
import BillingsTaxiTaxiContributionsPenaltyTableComponent from "../../common/contributions/penalty/table/billings-taxi-taxi-contributions-penalty-table.component";
import billingsTaxiTaxiContributionsBonusHelper from "../../common/contributions/bonus/billings-taxi-taxi-contributions-bonus.helper";
import billingsTaxiTaxiContributionsPenaltyHelper from "../../common/contributions/penalty/billings-taxi-taxi-contributions-penalty.helper";
import BillingsTaxiTaxiDetails, {
  BillingsTaxiTaxiDetailsContribution,
  BillingsTaxiTaxiDetailsContributionBonus,
  BillingsTaxiTaxiDetailsContributionPenalty,
  BillingsTaxiTaxiDetailsContributionType,
  BillingsTaxiTaxiDetailsNode,
} from "../../../../../common/services/billings/taxi-taxi/details/billings-taxi-taxi-details";
import BillingStatus from "../../../types/billing-status";

const createBillingNode = (
  node: BillingsTaxiTaxiDetailsNode,
  index: number,
  nodes: BillingsTaxiTaxiDetailsNode[],
) => {
  const previousItem = index - 1 >= 0 ? nodes[index - 1] : null;

  const url =
    previousItem !== null
      ? googleMapsRouteService.createGoogleMapsRouteUrl([
          { latitude: previousItem.lat, longitude: previousItem.lon },
          { latitude: node.lat, longitude: node.lon },
        ])
      : "";

  const result: BillingsNode = {
    id: node.uuid,
    nodeId: node.nodeUuid,
    lat: node.lat,
    lon: node.lon,
    description: node.description,
    checkoutDate: node.checkoutDate,
    checkoutEventUuid: node.checkoutEventUuid,
    plannedDate: node.plannedDate,
    plannedDistance: node.plannedDistance,
    orderId: node.orderUuid,
    distance: node.distance,
    haltingTime: node.haltingTime,
    highwayCharge: node.highwayCharge,
    isHighwayAllowed: node.allowCharge,
    isEditable: node.editableCoordinates,
    googleMapsUrl: url,
    position: index + 1,
  };

  return result;
};

const createBillingNodes = (
  nodes: BillingsTaxiTaxiDetailsNode[],
): BillingsNode[] => {
  return nodes.map(createBillingNode);
};

const createBillingPenaltyContributionType = (
  contributionType:
    | BillingsTaxiTaxiDetailsContributionType.PENALTY_WRONG_ROUTE
    | BillingsTaxiTaxiDetailsContributionType.PENALTY_BEING_LATE
    | BillingsTaxiTaxiDetailsContributionType.PENALTY_INCOMPATIBLE_CAR
    | BillingsTaxiTaxiDetailsContributionType.PENALTY_OTHER,
): BillingsTaxiTaxiContributionPenaltyType => {
  switch (contributionType) {
    case BillingsTaxiTaxiDetailsContributionType.PENALTY_WRONG_ROUTE:
      return BillingsTaxiTaxiContributionPenaltyType.PENALTY_WRONG_ROUTE;
    case BillingsTaxiTaxiDetailsContributionType.PENALTY_BEING_LATE:
      return BillingsTaxiTaxiContributionPenaltyType.PENALTY_BEING_LATE;
    case BillingsTaxiTaxiDetailsContributionType.PENALTY_INCOMPATIBLE_CAR:
      return BillingsTaxiTaxiContributionPenaltyType.PENALTY_INCOMPATIBLE_CAR;
    case BillingsTaxiTaxiDetailsContributionType.PENALTY_OTHER:
      return BillingsTaxiTaxiContributionPenaltyType.PENALTY_OTHER;
  }
};

const createBillingBonusContribution = (
  contribution: BillingsTaxiTaxiDetailsContributionBonus,
): BillingsTaxiTaxiContributionBonus => {
  switch (contribution.type) {
    case BillingsTaxiTaxiDetailsContributionType.BONUS_FAVORABLE_DISTANCE:
      return {
        type: BillingsTaxiTaxiContributionBonusType.BONUS_FAVORABLE_DISTANCE,
        comment: contribution.comment ?? "",
        distance: contribution.distance,
        rate: contribution.rate,
      };
    case BillingsTaxiTaxiDetailsContributionType.BONUS_OTHER:
      return {
        type: BillingsTaxiTaxiContributionBonusType.BONUS_OTHER,
        comment: contribution.comment ?? "",
        amount: contribution.amount,
        distance: null,
      };
  }
};

const createBillingPenaltyContribution = (
  contribution: BillingsTaxiTaxiDetailsContributionPenalty,
): BillingsTaxiTaxiContributionPenalty => {
  return {
    type: createBillingPenaltyContributionType(contribution.type),
    comment: contribution.comment ?? "",
    amount: contribution.amount,
  };
};

const createBillingContributions = (
  contributions: BillingsTaxiTaxiDetailsContribution[],
): BillingFormDataContributions => {
  const bonusContributions = contributions.filter(
    (contribution) =>
      contribution &&
      [
        BillingsTaxiTaxiDetailsContributionType.BONUS_FAVORABLE_DISTANCE,
        BillingsTaxiTaxiDetailsContributionType.BONUS_OTHER,
      ].includes(contribution.type),
  ) as BillingsTaxiTaxiDetailsContributionBonus[];

  const penaltyContributions = contributions.filter(
    (contribution) =>
      contribution &&
      [
        BillingsTaxiTaxiDetailsContributionType.PENALTY_BEING_LATE,
        BillingsTaxiTaxiDetailsContributionType.PENALTY_INCOMPATIBLE_CAR,
        BillingsTaxiTaxiDetailsContributionType.PENALTY_OTHER,
        BillingsTaxiTaxiDetailsContributionType.PENALTY_WRONG_ROUTE,
      ].includes(contribution.type),
  ) as BillingsTaxiTaxiDetailsContributionPenalty[];

  return {
    bonus: bonusContributions.map((contribution) =>
      createBillingBonusContribution(contribution),
    ),
    penalty: penaltyContributions.map((contribution) =>
      createBillingPenaltyContribution(contribution),
    ),
  };
};

const createBillingData = (data: BillingsTaxiTaxiDetails) => {
  const billingDraft: BillingFormData = {
    orderId: data.orderForwardingAction.transportingOrder.cargoOrder.uuid,
    forwardingId: data.orderForwardingAction.uuid,
    billingNodes: createBillingNodes(data.billingNodes),
    billingContributions: createBillingContributions(data.billingContributions),
    contractDetails: {
      companyName: data.orderForwardingAction.contract.producerTaxi.displayName,
      haltingTimeRate: data.orderForwardingAction.contract.haltingTimeRate,
      distanceRate: data.orderForwardingAction.contract.distanceRate,
      model: data.orderForwardingAction.contract.model,
      haltingTimeAppliedAfter:
        data.orderForwardingAction.contract.haltingTimeAfterMinutes,
    },
    amountForChargeHaltings: data.amountForChargeHaltings,
    amountForChargeHighways: data.amountForChargeHighways,
    distance: data.distance,
    amountForDistance: data.amountForDistance,
    internalOrderId:
      data.orderForwardingAction.transportingOrder.cargoOrder.humanId,
    allContributionsAmount: data.allContributionsAmount,
    planEntryId: data.planEntryUuid,
    sumOfBonuses: data.sumOfBonuses,
    sumOfPenalties: data.sumOfPenalties,
    date: data.date,
    status: data.status,
    producerTaxiUuid: data.orderForwardingAction.contract.producerTaxi.uuid,
    consumerTaxiUuid: data.orderForwardingAction.contract.consumerTaxi.uuid,
    passengers: data.passengers,
  };

  return billingDraft;
};

const createContributionsBonusTableRow = (
  contribution: BillingsTaxiTaxiContributionBonus,
  contributionIndex: number,
): BillingsTaxiTaxiContributionsBonusTableRow => {
  const translations =
    billingsTranslationsHelper.getTaxiTaxiContributionsTranslations();

  const contributionType =
    billingsTaxiTaxiContributionsBonusHelper.getContributionTypeTranslation(
      contribution.type,
    );

  switch (contribution.type) {
    case BillingsTaxiTaxiContributionBonusType.BONUS_FAVORABLE_DISTANCE:
      return {
        id: `${contribution.type}-${contributionIndex}`,
        value: {
          type: <div title={contributionType}>{contributionType}</div>,
          distance: (
            <div title={String(contribution.distance)}>
              {String(contribution.distance)}
            </div>
          ),
          rate: (
            <div title={String(contribution.rate)}>
              {String(contribution.rate)}
            </div>
          ),
          amount: (
            <div title={translations.attributeNotApplicable}>
              {translations.attributeNotApplicable}
            </div>
          ),
          comment: (
            <div title={contribution.comment}>{contribution.comment}</div>
          ),
          actions: <div className="d-flex"></div>,
        },
      };
    case BillingsTaxiTaxiContributionBonusType.BONUS_OTHER:
      return {
        id: `${contribution.type}-${contributionIndex}`,
        value: {
          type: <div title={contributionType}>{contributionType}</div>,
          distance: (
            <div title={translations.attributeNotApplicable}>
              {translations.attributeNotApplicable}
            </div>
          ),
          rate: (
            <div title={translations.attributeNotApplicable}>
              {translations.attributeNotApplicable}
            </div>
          ),
          amount: (
            <div title={String(contribution.amount)}>
              {String(contribution.amount)}
            </div>
          ),
          comment: (
            <div title={contribution.comment}>{contribution.comment}</div>
          ),
          actions: <div className="d-flex"></div>,
        },
      };
    default:
      return {
        id: String(contributionIndex),
        value: {
          type: <div></div>,
          distance: <div></div>,
          rate: <div></div>,
          amount: <div></div>,
          comment: <div></div>,
          actions: <div className="d-flex"></div>,
        },
      };
  }
};

const createContributionsPenaltyTableRow = (
  contribution: BillingsTaxiTaxiContributionPenalty,
  contributionIndex: number,
): BillingsTaxiTaxiContributionsPenaltyTableRow => {
  const contributionType =
    billingsTaxiTaxiContributionsPenaltyHelper.getContributionTypeTranslation(
      contribution.type,
    );

  return {
    id: `${contribution.type}-${contributionIndex}`,
    value: {
      type: <div title={contributionType}>{contributionType}</div>,
      amount: (
        <div title={String(contribution.amount)}>
          {String(contribution.amount)}
        </div>
      ),
      comment: <div title={contribution.comment}>{contribution.comment}</div>,
      actions: <div className="d-flex"></div>,
    },
  };
};

const createTabsData = (
  contributions: BillingFormDataContributions,
  isDataLoading: boolean,
): TabsData => {
  const translations =
    billingsTranslationsHelper.getTaxiTaxiContributionsTranslations();

  const bonusTableRows: BillingsTaxiTaxiContributionsBonusTableRow[] =
    contributions.bonus.map((contribution, index) =>
      createContributionsBonusTableRow(contribution, index),
    );

  const penaltyTableRows: BillingsTaxiTaxiContributionsPenaltyTableRow[] =
    contributions.penalty.map((contribution, index) =>
      createContributionsPenaltyTableRow(contribution, index),
    );

  const BonusTableContent = (
    <BillingsTaxiTaxiContributionsBonusTableComponent
      isLoading={isDataLoading}
      rows={bonusTableRows}
    />
  );

  const PenaltyTableContent = (
    <BillingsTaxiTaxiContributionsPenaltyTableComponent
      isLoading={isDataLoading}
      rows={penaltyTableRows}
    />
  );

  const tabsData: TabsData = [
    {
      label: translations.bonus.title,
      content: BonusTableContent,
      counter: contributions.bonus.filter((contribution) => contribution.type)
        .length,
      totalTableRows: bonusTableRows.length,
    },
    {
      label: translations.penalty.title,
      content: PenaltyTableContent,
      counter: contributions.penalty.filter((contribution) => contribution.type)
        .length,
      totalTableRows: penaltyTableRows.length,
    },
  ];

  return tabsData;
};

const billingDataFactory = {
  createBillingData,
  createTabsData,
};

export default billingDataFactory;
